import {
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'

import PlusIcon from '@/assets/icons/plus.svg?react'
import ReduceIcon from '@/assets/icons/reduce.svg?react'
import { Button, Modal, Trans } from '@/components'
import { Machine } from '@/models/machine'
import { getExtraSpecificationActionState } from '@/pages/create-instance/extraSpecificationActions'
import {
  EMPTY_EXTRA_RESOURCE_CONFIG,
  ExtraResourceConfig,
  getAvailableExtraCpuCores,
  getAvailableExtraRamGB,
  getAvailableExtraStorageGB,
  getExtraCpuStep,
  getExtraDiskStepGB,
  getExtraRamGBRange,
  getExtraRamStep,
  getMaxExtraCpuCores,
  getMinExtraStorageGB,
  hasExtraResourceConfig,
  isExtraRamBoundToCpu,
  type MinResourceConfig,
  normalizeExtraResourceConfig,
} from '@/pages/create-instance/utils/extraStorage'
import { cn } from '@/utils/style'

import { MinimumResourceRequirementText } from './minimum-resource-badge'
import { SelectMachineTooltip } from './SelectMachineTooltip'

const ENABLE_EXTRA_CPU = true
const ENABLE_EXTRA_RAM = true
const ENABLE_EXTRA_DISK = true

type ResourceStepperProps = {
  value: number
  min?: number
  max: number
  step: number
  decreaseStep?: number
  unit: string
  disabled?: boolean
  editable?: boolean
  onChange: (value: number) => void
}

function ResourceStepper({
  value,
  min = 0,
  max,
  step,
  decreaseStep = step,
  unit,
  disabled = false,
  editable = true,
  onChange,
}: ResourceStepperProps) {
  const [inputValue, setInputValue] = useState(String(value))
  const previousValue = Math.max(value - decreaseStep, min)
  const nextValue = Math.min(value + step, max)
  const canDecrease = !disabled && previousValue < value
  const canIncrease = !disabled && nextValue > value

  const ceilToStep = (rawValue: number) => {
    if (step <= 0) return rawValue

    return Math.ceil(rawValue / step) * step
  }

  useEffect(() => {
    setInputValue(String(value))
  }, [value])

  const clampValue = (rawValue: number) => {
    const nextIntegerValue = Math.floor(rawValue)
    const steppedValue = ceilToStep(nextIntegerValue)

    return Math.min(Math.max(steppedValue, min), max)
  }

  const commitInputValue = () => {
    const nextValue = inputValue === '' ? min : Number(inputValue)
    const clampedValue = clampValue(Number.isFinite(nextValue) ? nextValue : min)

    setInputValue(String(clampedValue))
    onChange(clampedValue)
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value

    if (/^\d*$/.test(nextValue)) {
      const numericValue = Number(nextValue)

      setInputValue(nextValue !== '' && numericValue > max ? String(max) : nextValue)
    }
  }

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur()
    }
  }

  return (
    <div className="flex items-start rounded-lg bg-panel-gray-100 p-0.5">
      <button
        type="button"
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-md bg-panel-white shadow-card-100',
          canDecrease ? 'cursor-pointer' : 'cursor-not-allowed',
        )}
        disabled={!canDecrease}
        onClick={() => onChange(previousValue)}
      >
        <ReduceIcon className={canDecrease ? 'text-primary' : 'text-tertiary'} width={20} />
      </button>
      <div className="t-sb flex h-10 min-w-[80px] items-center justify-center gap-1 px-4 text-center">
        <input
          className={cn(
            'min-w-0 max-w-[56px] bg-transparent text-center outline-none',
            disabled ? 'text-tertiary' : 'text-primary',
            !editable && 'cursor-default',
          )}
          disabled={disabled}
          readOnly={!editable}
          inputMode="numeric"
          min={min}
          max={max}
          step={step}
          value={inputValue}
          onBlur={editable ? commitInputValue : undefined}
          onChange={editable ? handleInputChange : undefined}
          onKeyDown={editable ? handleInputKeyDown : undefined}
        />
        <span className="text-tertiary">{unit}</span>
      </div>
      <button
        type="button"
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-md bg-panel-white shadow-card-100',
          canIncrease ? 'cursor-pointer' : 'cursor-not-allowed',
        )}
        disabled={!canIncrease}
        onClick={() => onChange(nextValue)}
      >
        <PlusIcon className={canIncrease ? 'text-primary' : 'text-tertiary'} width={20} />
      </button>
    </div>
  )
}

type ExtraResourceItem = {
  key: keyof ExtraResourceConfig
  label: ReactNode
  value: number
  min?: number
  max: number
  step: number
  decreaseStep?: number
  unit: string
  editable?: boolean
  visible: boolean
}

export type ExtraResourceChangeReason = 'user' | 'reset'

function DiscardExtraSpecificationsModal({
  open,
  onClose,
  onDiscard,
}: {
  open: boolean
  onClose: () => void
  onDiscard: () => void
}) {
  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>
        <Trans i18nKey="create.discardExtraSpecificationsTitle">
          Discard extra specifications?
        </Trans>
      </Modal.Header>
      <Modal.Body>
        <Trans i18nKey="create.discardExtraSpecificationsDescription">
          Your extra configuration will be removed. Are you sure you want to revert to the basic
          specification?
        </Trans>
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" className="min-w-[88px]" onClick={onClose}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button type="danger" className="min-w-[88px]" onClick={onDiscard}>
          <Trans i18nKey="create.discardExtraSpecificationsConfirm">Discard</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export function ExtraSpecificationSection({
  machine,
  value = EMPTY_EXTRA_RESOURCE_CONFIG,
  minimumExtraResourceConfig = EMPTY_EXTRA_RESOURCE_CONFIG,
  minimumResource,
  minimumDiskGB = 0,
  minimumRequirementSource = 'image',
  onChange,
}: {
  machine?: Machine
  value?: ExtraResourceConfig
  minimumExtraResourceConfig?: ExtraResourceConfig
  minimumResource?: MinResourceConfig
  minimumDiskGB?: number
  minimumRequirementSource?: 'image' | 'snapshot'
  onChange: (value: ExtraResourceConfig, reason: ExtraResourceChangeReason) => void
}) {
  const [isConfiguring, setIsConfiguring] = useState(false)
  const [isDiscardOpen, setIsDiscardOpen] = useState(false)

  const normalizedValue = useMemo(
    () => normalizeExtraResourceConfig(machine, value),
    [machine, value],
  )
  const hasConfig = hasExtraResourceConfig(normalizedValue)
  const ramBoundToCpu = isExtraRamBoundToCpu()
  const { step: cpuStepCores, decreaseStep: cpuDecreaseStepCores } = getExtraCpuStep(
    normalizedValue.cpuCores ?? 0,
  )
  const { step: ramStepGB, decreaseStep: ramDecreaseStepGB } = getExtraRamStep(
    normalizedValue.ramGB ?? 0,
  )
  const diskStepGB = getExtraDiskStepGB()
  const availableCpuCores = getAvailableExtraCpuCores(machine)
  const availableRamGB = getAvailableExtraRamGB(machine)
  const maxCpuCores = getMaxExtraCpuCores(machine)
  const ramGBRange = getExtraRamGBRange(machine, normalizedValue.cpuCores ?? 0)
  const maxRamGB = ramGBRange.max
  const maxDiskGB = getAvailableExtraStorageGB(machine)
  const normalizedMinimumConfig = useMemo(
    () => normalizeExtraResourceConfig(machine, minimumExtraResourceConfig),
    [machine, minimumExtraResourceConfig],
  )
  const minCpuCores = normalizedMinimumConfig.cpuCores ?? 0
  const minRamGB = normalizedMinimumConfig.ramGB ?? 0
  const minDiskGB = getMinExtraStorageGB(machine, minimumDiskGB)
  const effectiveMinimumConfig = useMemo(
    () =>
      normalizeExtraResourceConfig(machine, {
        ...normalizedMinimumConfig,
        diskGB: minDiskGB,
      }),
    [machine, minDiskGB, normalizedMinimumConfig],
  )
  const { showMinimumRequirementNotice, resetDisabled, discardDisabled } =
    getExtraSpecificationActionState(normalizedValue, effectiveMinimumConfig)
  const hasAvailableExtraResource = availableCpuCores > 0 || availableRamGB > 0 || maxDiskGB > 0

  useEffect(() => {
    if (hasConfig) {
      setIsConfiguring(true)
    }
  }, [hasConfig])

  const resourceItems: ExtraResourceItem[] = [
    {
      key: 'cpuCores',
      label: (
        <Trans i18nKey="create.extraSpecCpuStep" values={{ step: cpuStepCores }}>
          Extra vCPU (Step: {'{{step}}'} Cores)
        </Trans>
      ),
      value: normalizedValue.cpuCores ?? 0,
      min: minCpuCores,
      max: maxCpuCores,
      step: cpuStepCores,
      decreaseStep: cpuDecreaseStepCores,
      unit: 'Cores',
      visible: ENABLE_EXTRA_CPU,
    },
    {
      key: 'ramGB',
      label: (
        <Trans i18nKey="create.extraSpecRamStep" values={{ step: ramStepGB }}>
          Extra RAM (Step: {'{{step}}'} GB)
        </Trans>
      ),
      value: normalizedValue.ramGB ?? 0,
      min: Math.max(ramGBRange.min, minRamGB),
      max: maxRamGB,
      step: ramStepGB,
      decreaseStep: ramDecreaseStepGB,
      unit: 'GB',
      visible: ENABLE_EXTRA_RAM,
    },
    {
      key: 'diskGB',
      label: (
        <Trans i18nKey="create.extraSpecStorageStep" values={{ step: diskStepGB }}>
          Extra Storage (Step: {'{{step}}'} GB)
        </Trans>
      ),
      value: normalizedValue.diskGB ?? 0,
      min: minDiskGB,
      max: maxDiskGB,
      step: diskStepGB,
      unit: 'GB',
      visible: ENABLE_EXTRA_DISK,
    },
  ]

  const visibleResourceItems = resourceItems.filter((item) => item.visible)

  const updateResource = (key: keyof ExtraResourceConfig, nextValue: number) => {
    // Under the ratio rule RAM cannot exist without CPU: clearing one clears the other.
    if (ramBoundToCpu && key === 'cpuCores' && nextValue <= 0) {
      onChange(
        normalizeExtraResourceConfig(machine, {
          ...normalizedValue,
          cpuCores: undefined,
          ramGB: undefined,
        }),
        'user',
      )
      return
    }

    if (ramBoundToCpu && key === 'ramGB' && nextValue <= 0) {
      onChange(
        normalizeExtraResourceConfig(machine, {
          ...normalizedValue,
          cpuCores: undefined,
          ramGB: undefined,
        }),
        'user',
      )
      return
    }

    onChange(
      normalizeExtraResourceConfig(machine, {
        ...normalizedValue,
        [key]: nextValue > 0 ? nextValue : undefined,
      }),
      'user',
    )
  }

  const resetResources = () => {
    onChange(effectiveMinimumConfig, 'reset')
  }

  const discardResources = () => {
    onChange(effectiveMinimumConfig, 'reset')
    setIsConfiguring(hasExtraResourceConfig(effectiveMinimumConfig))
    setIsDiscardOpen(false)
  }

  return (
    <div className="mt-8 px-8">
      <div className="flex flex-col items-start gap-2">
        <div className="text-secondary t-sb">
          <Trans i18nKey="create.extraSpecification">Extra Specification</Trans>
        </div>

        {isConfiguring ? (
          <div className="flex w-full flex-col gap-4 rounded-lg border border-neutral-200 bg-panel-white p-4 shadow-card-100">
            <div className="flex flex-wrap items-start gap-4 rounded-lg">
              {visibleResourceItems.map((item) => (
                <div
                  key={item.key}
                  className="flex min-w-[234px] flex-1 flex-col items-start gap-2"
                >
                  <div className="t-xs text-secondary">{item.label}</div>
                  <SelectMachineTooltip enabled={!machine}>
                    <ResourceStepper
                      value={item.value}
                      min={item.min}
                      max={item.max}
                      step={item.step}
                      decreaseStep={item.decreaseStep}
                      unit={item.unit}
                      editable={item.editable}
                      disabled={!machine || item.max <= 0}
                      onChange={(nextValue) => updateResource(item.key, nextValue)}
                    />
                  </SelectMachineTooltip>
                </div>
              ))}
            </div>
            <div className="flex w-full flex-col gap-2 border-t border-neutral-200 pt-3">
              {showMinimumRequirementNotice && (
                <div className="t-xs text-secondary">
                  <span>
                    {minimumRequirementSource === 'snapshot' ? (
                      <Trans i18nKey="create.extraSpecificationMinimumSnapshotRequirement">
                        This specification is the minimum requirement for the selected snapshot.
                      </Trans>
                    ) : (
                      <Trans i18nKey="create.extraSpecificationMinimumImageRequirement">
                        This specification is the minimum requirement for the selected image.
                      </Trans>
                    )}
                  </span>
                  {minimumResource ? (
                    <>
                      {' '}
                      <MinimumResourceRequirementText resource={minimumResource} />
                    </>
                  ) : null}
                </div>
              )}
              <div className="t-sb flex items-start gap-4 whitespace-nowrap">
                <button
                  type="button"
                  className={cn(
                    'text-highlight',
                    resetDisabled && 'cursor-not-allowed text-tertiary',
                  )}
                  disabled={resetDisabled}
                  onClick={resetResources}
                >
                  <Trans i18nKey="common.reset">Reset</Trans>
                </button>
                <button
                  type="button"
                  className={cn(
                    'text-error',
                    discardDisabled && 'cursor-not-allowed text-tertiary',
                  )}
                  disabled={discardDisabled}
                  onClick={() => setIsDiscardOpen(true)}
                >
                  <Trans i18nKey="common.discard">Discard</Trans>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <SelectMachineTooltip enabled={!machine}>
            <Button
              type="outline"
              className="min-w-[88px]"
              disabled={!machine || !hasAvailableExtraResource}
              onClick={() => {
                setIsConfiguring(true)
                onChange(effectiveMinimumConfig, 'user')
              }}
            >
              <Trans i18nKey="create.addExtra">Add Extra</Trans>
            </Button>
          </SelectMachineTooltip>
        )}

        {machine && (
          <div className="t-sm text-secondary">
            <Trans i18nKey="create.extraSpecificationDescription">
              The final instance specification is calculated as (Basic Spec x Unit Qty) + Extra
              Spec.
            </Trans>
          </div>
        )}
      </div>

      <DiscardExtraSpecificationsModal
        open={isDiscardOpen}
        onClose={() => setIsDiscardOpen(false)}
        onDiscard={discardResources}
      />
    </div>
  )
}
